/**
 * Route Analysis Utilities
 * Handles route segment calculation and shared segment detection for trip joining logic
 */

// Define the route segments for each direction
const ROUTE_SEGMENTS = {
  NairobiToMoshi: [
    { from: 'Nairobi', to: 'Arusha', segment: 'NairobiToArusha' },
    { from: 'Arusha', to: 'Moshi', segment: 'ArushaToMoshi' }
  ],
  MoshiToNairobi: [
    { from: 'Moshi', to: 'Arusha', segment: 'MoshiToArusha' },
    { from: 'Arusha', to: 'Nairobi', segment: 'ArushaToNairobi' }
  ]
};

/** Map stored segment keys to city endpoints (used when public view omits pickup/dropoff). */
const SEGMENT_ENDPOINTS = {
  NairobiToArusha: { pickup: 'Nairobi', dropoff: 'Arusha' },
  ArushaToMoshi: { pickup: 'Arusha', dropoff: 'Moshi' },
  NairobiToMoshi: { pickup: 'Nairobi', dropoff: 'Moshi' },
  MoshiToArusha: { pickup: 'Moshi', dropoff: 'Arusha' },
  ArushaToNairobi: { pickup: 'Arusha', dropoff: 'Nairobi' },
  MoshiToNairobi: { pickup: 'Moshi', dropoff: 'Nairobi' },
};

function participantRoute(participant, tripDirection) {
  if (participant?.pickup_location && participant?.dropoff_location) {
    return {
      pickup: participant.pickup_location,
      dropoff: participant.dropoff_location,
      direction: tripDirection,
    };
  }
  const endpoints = SEGMENT_ENDPOINTS[participant?.segment];
  if (!endpoints) return null;
  return { ...endpoints, direction: tripDirection };
}

export const getRouteSegments = (pickup, dropoff, direction) => {
  const segments = ROUTE_SEGMENTS[direction] || [];
  const routeSegments = [];

  let includeSegment = false;

  for (const segment of segments) {
    if (segment.from === pickup) {
      includeSegment = true;
    }

    if (includeSegment) {
      routeSegments.push(segment);
    }

    if (segment.to === dropoff) {
      break;
    }
  }

  return routeSegments;
};

export const hasSharedSegments = (route1, route2) => {
  if (route1.direction !== route2.direction) {
    return false;
  }

  const segments1 = getRouteSegments(route1.pickup, route1.dropoff, route1.direction);
  const segments2 = getRouteSegments(route2.pickup, route2.dropoff, route2.direction);

  for (const seg1 of segments1) {
    for (const seg2 of segments2) {
      if (seg1.segment === seg2.segment) {
        return true;
      }
    }
  }

  return false;
};

export const canJoinTrip = (trip, existingParticipants, joinerRoute) => {
  if (joinerRoute.direction !== trip.direction) {
    return false;
  }

  const nonCreatorParticipants = existingParticipants.filter(p => !p.isCreator);

  if (nonCreatorParticipants.length === 0) {
    const creator = existingParticipants.find(p => p.isCreator);
    if (!creator) {
      return false;
    }

    const creatorRoute = participantRoute(creator, trip.direction);
    if (!creatorRoute) {
      return false;
    }

    return hasSharedSegments(joinerRoute, creatorRoute);
  }

  for (const participant of nonCreatorParticipants) {
    const participantRouteObj = participantRoute(participant, trip.direction);
    if (!participantRouteObj) continue;

    if (hasSharedSegments(joinerRoute, participantRouteObj)) {
      return true;
    }
  }

  return false;
};

export const getAvailableRoutesForTrip = (trip, existingParticipants) => {
  const availableRoutes = [];
  const direction = trip.direction;

  const allPossibleRoutes = [];

  if (direction === 'NairobiToMoshi') {
    allPossibleRoutes.push(
      { pickup: 'Nairobi', dropoff: 'Arusha' },
      { pickup: 'Nairobi', dropoff: 'Moshi' },
      { pickup: 'Arusha', dropoff: 'Moshi' }
    );
  } else if (direction === 'MoshiToNairobi') {
    allPossibleRoutes.push(
      { pickup: 'Moshi', dropoff: 'Arusha' },
      { pickup: 'Moshi', dropoff: 'Nairobi' },
      { pickup: 'Arusha', dropoff: 'Nairobi' }
    );
  }

  const tripOrigin = trip.pickup_location;

  for (const route of allPossibleRoutes) {
    // Origin-only: joiners must board at the trip origin (mid-route drop-offs are fine).
    if (tripOrigin && route.pickup !== tripOrigin) {
      continue;
    }

    const joinerRoute = {
      ...route,
      direction: direction
    };

    if (canJoinTrip(trip, existingParticipants, joinerRoute)) {
      availableRoutes.push(route);
    }
  }

  return availableRoutes;
};

export const getSharedSegments = (route1, route2) => {
  if (route1.direction !== route2.direction) {
    return [];
  }

  const segments1 = getRouteSegments(route1.pickup, route1.dropoff, route1.direction);
  const segments2 = getRouteSegments(route2.pickup, route2.dropoff, route2.direction);

  const sharedSegments = [];

  for (const seg1 of segments1) {
    for (const seg2 of segments2) {
      if (seg1.segment === seg2.segment) {
        sharedSegments.push(seg1);
      }
    }
  }

  return sharedSegments;
};

export const formatRoute = (pickup, dropoff) => {
  return `${pickup} → ${dropoff}`;
};

export const getRouteDescription = (pickup, dropoff, direction) => {
  const segments = getRouteSegments(pickup, dropoff, direction);
  const segmentNames = segments.map(s => s.segment);

  return {
    route: formatRoute(pickup, dropoff),
    segments: segments,
    segmentNames: segmentNames,
    isDirect: segments.length === 1,
    isMultiSegment: segments.length > 1
  };
};

export const segmentsOverlap = (segment1, segment2, direction) => {
  const forwardSegments = {
    'NairobiToArusha': ['NairobiToArusha'],
    'ArushaToMoshi': ['ArushaToMoshi'],
    'NairobiToMoshi': ['NairobiToArusha', 'ArushaToMoshi']
  };

  const reverseSegments = {
    'MoshiToArusha': ['MoshiToArusha'],
    'ArushaToNairobi': ['ArushaToNairobi'],
    'MoshiToNairobi': ['MoshiToArusha', 'ArushaToNairobi']
  };

  const segments = direction.includes('ToMoshi') ? forwardSegments : reverseSegments;

  const segment1Parts = segments[segment1] || [];
  const segment2Parts = segments[segment2] || [];

  return segment1Parts.some(part1 =>
    segment2Parts.some(part2 => part1 === part2)
  );
};
